/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  TrackInfo,
  RequestType,
  SeekRangeInfo,
  StreamTimelineEvent,
  Action,
  StreamTimelineMetadata,
  PlayerContext,
  ThumbnailPreviewMetadata,
  PlaybackOptions,
  TrackVariantInfo,
  TrackType,
  AdTechnology,
} from '@quickplay/player-interface';
import { CastPlayer } from '../CastPlayer';
import {
  AnyFunc,
  createEventEmitter,
  createLogger,
  EventEmitter,
  eventObject,
  Logger,
  Error,
  createError,
} from '@quickplay/foundation';
import { PlatformAsset } from '@quickplay/content-authorization';
import { CastManager } from '../fl-chrome-sender';
import { CastPlaybackOptions } from '../CastPlaybackOptions';
import { advertBreakDurationsPriorTo } from '../PlaybackPolicyUtil';

export function createCastPlayerImpl(castManager: CastManager): CastPlayer {
  const logger: Logger = createLogger();
  const eventEmitter: EventEmitter = createEventEmitter();
  let platformAsset_: PlatformAsset;
  let mediaSession: any;
  let playerContext: PlayerContext;
  let adBreaks: any;

  //@ts-ignore
  const remotePlayer = isCastDefined() && new cast.framework.RemotePlayer();
  //@ts-ignore
  const remotePlayerController = isCastDefined() && new cast.framework.RemotePlayerController(remotePlayer);

  const castPlayer: CastPlayer = {
    get platformAsset() {
      return platformAsset_;
    },

    async loadMedia(platformAsset: PlatformAsset, castPlaybackOptions: CastPlaybackOptions = {}): Promise<void> {
      if (!isCastDefined()) {
        logger.debug(
          'cast framework is not available. Ensure the cast framework is loaded properly and running on chrome browser.',
        );
        return;
      }
      try {
        const userAuthToken = await castManager.getUserAuthToken();
        const customData = {
          castAsset: {
            userAuthToken: userAuthToken,
            platformAsset: platformAsset,
            headers: castPlaybackOptions.headers,
            flAnalyticsData: castPlaybackOptions.flAnalyticsData || {},
            videoAnalyticsData: castPlaybackOptions.videoAnalyticsData || {},
            customAdMetadata: castPlaybackOptions.customAdMetadata || {},
            bookmarkData: castPlaybackOptions.bookmarkData || {},
            metadata: castPlaybackOptions.metadata,
          },
        };
        //@ts-ignore
        const mediaInfo = new chrome.cast.media.MediaInfo();
        mediaInfo.metadata = castPlaybackOptions.metadata;
        mediaInfo.tracks = castPlaybackOptions.mediaTracks;
        mediaInfo.customData = customData;
        //@ts-ignore
        const request = new chrome.cast.media.LoadRequest(mediaInfo);
        if (castPlaybackOptions.initialPlaybackTime || castPlaybackOptions.initialPlaybackTime === 0) {
          request.currentTime = castPlaybackOptions.initialPlaybackTime;
        }
        subscribeCustomChannelEvents();
        castManager.castSessionManager.loadMedia(request).then(
          function () {
            eventEmitter.publish('contentloaded');
            setupRemotePlayerController();
          },
          function (errorCode: any) {
            logger.error('Failed to Loadmedia', errorCode);
            unsubscribeCustomChannelEvents();
          },
        );
      } catch (e) {
        logger.error('Failed to Loadmedia', e);
      }
    },

    subscribe(eventName: string | eventObject, callback?: AnyFunc): void {
      eventEmitter.subscribe(eventName, callback);
    },

    unsubscribe(eventName: string | eventObject, callback?: AnyFunc): void {
      eventEmitter.unsubscribe(eventName, callback);
    },

    get playerState() {
      switch (remotePlayer.playerState) {
        case 'PLAYING':
          return 'started';

        case 'PAUSED':
          return 'paused';

        case 'IDLE':
          return 'idle';

        case 'BUFFERING':
          return 'started';

        default:
          return 'idle';
      }
    },

    get playbackRate() {
      return 0;
    },

    set playbackRate(_rate) {
      //no-op
    },

    get availableBitrates() {
      return [];
    },

    set minBitrate(_minBitrate) {
      //no-op
    },

    get minBitrate() {
      return 0;
    },

    set maxBitrate(_maxBitrate) {
      //no-op
    },

    get maxBitrate() {
      return 0;
    },

    get internalPlayerName() {
      return 'Chrome Sender';
    },

    get internalPlayerVersion() {
      return '';
    },

    get rawPlayer() {
      return remotePlayer;
    },

    get rawLibrary() {
      return undefined;
    },

    get isBuffering() {
      return false;
    },

    get isSeeking() {
      return false;
    },

    get currentTime() {
      if (remotePlayer.liveSeekableRange !== undefined) {
        return remotePlayer.currentTime;
      }

      const advertBreaksDuration = advertBreakDurationsPriorTo(remotePlayer.currentTime * 1000, adBreaks);
      return remotePlayer.currentTime - advertBreaksDuration / 1000;
    },

    get currentEpochTime() {
      return new Date(remotePlayer.currentTime).getTime();
    },

    get duration() {
      if (remotePlayer.liveSeekableRange !== undefined || !adBreaks) {
        return remotePlayer.duration;
      }

      const contentDuration =
        remotePlayer.duration * 1000 -
        adBreaks
          .filter((advertBreak: any) => advertBreak.durationMs > 0)
          .map((advertBreak: any) => advertBreak.durationMs)
          .reduce((a: any, b: any) => a + b, 0);

      return contentDuration / 1000;
    },

    get playbackStatistics() {
      return undefined;
    },

    get isLive() {
      return remotePlayer.liveSeekableRange !== undefined;
    },

    get isMute() {
      return remotePlayer.isMuted;
    },

    set volume(volumeLevel: number) {
      remotePlayer.volumeLevel = volumeLevel;
      remotePlayerController.setVolumeLevel();
    },

    get volume() {
      return remotePlayer.volumeLevel;
    },

    muteOrUnmute() {
      remotePlayerController.muteOrUnmute();
    },

    load(): void {
      //no-op
    },

    setRequestInterceptor(_requestInterceptor: (requestType: RequestType, request: any) => void): void {
      //no-op
    },

    setResponseInterceptor(_responseInterceptor: (requestType: RequestType, response: any) => void): void {
      //no-op
    },

    restrictQuality(enableRestriction: boolean): void {
      remotePlayerController.restrictQuality(enableRestriction);
    },

    availableMimeTypes(trackType: TrackType): Array<string> {
      return remotePlayerController.availableMimeTypes(trackType);
    },

    unloadAndSavePreload(): Promise<void> {
      // no-op
      return Promise.resolve();
    },

    restoreFromPreload(): void {
      // no-op
    },

    setPreferredMimeTypes(trackType: TrackType, mimeTypes: Array<string>): void {
      return remotePlayerController.setPreferredMimeTypes(trackType, mimeTypes);
    },

    stop(): Promise<void> {
      remotePlayerController.stop();
      unsubscribeCustomChannelEvents();
      // Note: CBT-7669 : To fix the multiple instance of remote player controller on each content playback
      // While connect and play.
      disposeRemotePlayerController();
      return Promise.resolve();
    },

    play(): void {
      if (remotePlayer.isPaused) {
        remotePlayerController.playOrPause();
      }
    },

    pause(): void {
      if (remotePlayer.canPause && !remotePlayer.isPaused) {
        remotePlayerController.playOrPause();
      }
    },

    abort: (error: Error) => {
      logger.log(error);
    },

    send: (eventName: StreamTimelineEvent, action: Action, metadata?: StreamTimelineMetadata): void => {
      logger.log(`streamTimelineEvent: ${eventName} Action: ${action} metadata: ${metadata}`);
    },

    seek(seekPoint: number): void {
      if (remotePlayer.canSeek && remotePlayer.liveSeekableRange !== undefined) {
        remotePlayer.currentTime = seekPoint;
        remotePlayerController.seek();
      } else {
        const position = castManager.castSessionManager.getMediaSession().getEstimatedTime() * 1000;
        const advertBreaksDuration = advertBreakDurationsPriorTo(position, adBreaks);

        remotePlayer.currentTime = seekPoint + advertBreaksDuration / 1000;
        remotePlayerController.seek();
      }
    },

    async getThumbnail(_position: number): Promise<ThumbnailPreviewMetadata | undefined> {
      return undefined;
    },

    seekToLiveEdge(): void {
      this.seek(remotePlayer.liveSeekableRange.end);
    },

    seekableRange(): SeekRangeInfo {
      return remotePlayer.liveSeekableRange
        ? { start: remotePlayer.liveSeekableRange.start, end: remotePlayer.liveSeekableRange.end }
        : { start: 0, end: this.duration };
    },

    currentOffsetFromLiveEdge(): number {
      return 0;
    },

    getAllTracks(): TrackInfo[] {
      if (!remotePlayer.mediaInfo) {
        return [];
      }

      const tracks: any[] = remotePlayer.mediaInfo.tracks;
      return tracks.map(track => {
        return { language: track.language, type: track.type.toLowerCase() };
      });
    },

    getActiveTracks(): TrackInfo[] {
      if (
        !remotePlayer.mediaInfo ||
        !castManager.castSessionManager ||
        !castManager.castSessionManager.getMediaSession()
      ) {
        return [];
      }

      const tracks: any[] = remotePlayer.mediaInfo.tracks;
      const activeTrackIds = castManager.castSessionManager.getMediaSession().activeTrackIds;
      return tracks
        .filter(track => activeTrackIds.includes(track.trackId))
        .map(track => {
          return { language: track.language, type: track.type.toLowerCase() };
        });
    },

    getVariantTracks(): TrackVariantInfo[] {
      return [];
    },

    getAvailableTrackVariants(_trackType: TrackType): TrackVariantInfo[] {
      return [];
    },

    selectTrack(_track?: TrackInfo): void {
      if (!isCastDefined() || !remotePlayer.mediaInfo) {
        return;
      }

      let activeTrackIds: number[] = [];
      const tracks: any[] = remotePlayer.mediaInfo.tracks;
      if (_track) {
        activeTrackIds = tracks
          .filter(track => _track.type.toLowerCase() === track.type.toLowerCase() && _track.language === track.language)
          .map(track => track.trackId);
      }

      //@ts-ignore
      const tracksInfoRequest = new chrome.cast.media.EditTracksInfoRequest(activeTrackIds);
      castManager.castSessionManager.getMediaSession().editTracksInfo(
        tracksInfoRequest,
        () => {
          logger.log('Successfully changed track');
        },
        (e: any) => {
          logger.log('Failed to edit tracks', e);
        },
      );
    },

    get remainingTime() {
      return 0;
    },

    get adInsertionType(): AdTechnology | undefined {
      return undefined;
    },

    skipAd(): void {
      remotePlayerController.skipAd();
    },

    discardAdBreak(): void {
      //no op
    },

    getCuePoints(): number[] {
      return [];
    },

    resize(_width: number, _height: number, _isFullscreen: boolean): void {
      // no-op
    },

    accessFunctionHandler(_: string): void {
      //no-op
    },

    get playerContext() {
      return playerContext;
    },

    setPlaybackOptions(playbackOptions: PlaybackOptions): void {
      logger.log(`playback options`, playbackOptions);
    },
  };

  function isCastDefined(): boolean {
    //@ts-ignore
    return typeof cast !== 'undefined';
  }

  const connectedChangeHandler = (): void => {
    if (remotePlayer.isConnected) {
      logger.log('RemotePlayerController: Player connected');
    } else if (!remotePlayer.isConnected) {
      logger.log('RemotePlayerController: Player disconnected');
    }
  };

  const stateChangeHandler = (): void => {
    if (castPlayer.playerState === 'idle' && mediaSession.idleReason === 'FINISHED') {
      eventEmitter.publish('streamended');
    }
    eventEmitter.publish('playbackstatechanged', castPlayer.playerState);
  };

  const progressChangeHandler = (): void => {
    eventEmitter.publish('progressupdate', castPlayer.currentTime, castPlayer.currentEpochTime, castPlayer.duration);
  };

  const seekChangeHandler = (): void => {
    eventEmitter.publish('seekingstatechanged');
  };

  const setupRemotePlayerController = (): void => {
    if (!isCastDefined()) {
      logger.debug(
        'cast framework is not available. Ensure the cast framework is loaded properly and running on chrome browser.',
      );
      return;
    }
    mediaSession = castManager.castSessionManager.getMediaSession();

    remotePlayerController.addEventListener(
      //@ts-ignore
      cast.framework.RemotePlayerEventType.IS_CONNECTED_CHANGED,
      connectedChangeHandler,
    );

    remotePlayerController.addEventListener(
      //@ts-ignore
      cast.framework.RemotePlayerEventType.CURRENT_TIME_CHANGED,
      progressChangeHandler,
    );

    remotePlayerController.addEventListener(
      //@ts-ignore
      cast.framework.RemotePlayerEventType.PLAYER_STATE_CHANGED,
      stateChangeHandler,
    );

    //@ts-ignore
    remotePlayerController.addEventListener(cast.framework.RemotePlayerEventType.CAN_SEEK_CHANGED, seekChangeHandler);
  };

  const disposeRemotePlayerController = (): void => {
    remotePlayerController.removeEventListener(
      //@ts-ignore
      cast.framework.RemotePlayerEventType.IS_CONNECTED_CHANGED,
      connectedChangeHandler,
    );

    remotePlayerController.removeEventListener(
      //@ts-ignore
      cast.framework.RemotePlayerEventType.PLAYER_STATE_CHANGED,
      stateChangeHandler,
    );

    remotePlayerController.removeEventListener(
      //@ts-ignore
      cast.framework.RemotePlayerEventType.CURRENT_TIME_CHANGED,
      progressChangeHandler,
    );

    remotePlayerController.removeEventListener(
      //@ts-ignore
      cast.framework.RemotePlayerEventType.CAN_SEEK_CHANGED,
      seekChangeHandler,
    );
  };

  const createFLError = (errorJson: any, internalError?: Error): Error | undefined => {
    if (!errorJson || !errorJson.errorCode) {
      return undefined;
    }
    return createError(errorJson.errorCode, errorJson.errorMessage, internalError, errorJson.contextDescription);
  };

  const customMessageHandler = (_channelName: any, customEvent: any): void => {
    const event = JSON.parse(customEvent);
    if (event.messageName.toLowerCase() === 'adbreaks') {
      eventEmitter.publish(event.messageName.toLowerCase(), event.message);
      adBreaks = event.message;
    }
    if (event.messageName.toLowerCase() === 'adstart') {
      playerContext = PlayerContext.AD;
    }
    if (event.messageName.toLowerCase() === 'adend') {
      playerContext = PlayerContext.MAIN;
    }
    if (event.messageName.toLowerCase() === 'adprogressupdate') {
      eventEmitter.publish(event.messageName.toLowerCase(), event.message.adInfo, event.message.progress);
    } else if (event.messageName.toLowerCase() === 'streamtimelineevent') {
      eventEmitter.publish(
        event.messageName.toLowerCase(),
        event.message.eventName,
        event.message.action,
        event.message.metadata,
      );
    } else if (event.messageType.toLowerCase() === 'error') {
      const errorJson = event.message;
      const internalError = createFLError(errorJson.internalError);
      const error = createFLError(errorJson, internalError);
      eventEmitter.publish(event.messageType.toLowerCase(), error);
    } else {
      eventEmitter.publish(event.messageName.toLowerCase(), event.message);
    }
  };

  const subscribeCustomChannelEvents = (): void => {
    if (castManager && castManager.castSessionManager) {
      castManager.castSessionManager.addMessageListener(castManager.getChannelNamespace(), customMessageHandler);
    }
  };

  const unsubscribeCustomChannelEvents = (): void => {
    if (castManager && castManager.castSessionManager) {
      castManager.castSessionManager.removeMessageListener(castManager.getChannelNamespace(), customMessageHandler);
    }
  };
  return castPlayer;
}
